import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import {
  Input,
  Select,
  Button,
  ButtonType
} from 'syngenta-digital-cropwise-react-ui-kit';
import EditIcon from 'assets/edit_outlined.svg?react';
import ChevronDown from 'assets/chevron_down.svg?react';
import ChevronUp from 'assets/chevron_up.svg?react';
import FormInputWithPrecisionDisplay from 'components/PriceUnitField/components/FormInputWithPrecisionDisplay';
import { applicationMethodOptions } from 'screens/Applications&WorkOrders/StepsLayout/Constants/selectOptions';
import {
  Product,
  PestOption,
  FormValues
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import getDropdownParentWithoutDetaching from 'screens/Applications&WorkOrders/StepsLayout/Helpers/getDropdownParentWithoutDetaching';
import { isLiquidWithTankInfo } from '../../../ProductsTable/helpers';
import FormSectionHeader from '../FormSectionHeader';
import { useProductCalculations } from '../../hooks/useProductCalculations';
import TargetPestSelectionDrawer from '../TargetPestSelectionDrawer';
import { APPLIED_AREA_TOOLTIP_TEXT } from '../../constants';
import './styles.css';

interface Props {
  className?: string;
  application?: FormValues;
}

const ProductOtherSection: React.FC<Props> = ({
  className = 'mt-4 mb-6',
  application
}) => {
  const {
    values: productToAdd,
    setFieldValue,
    handleChange
  } = useFormikContext<Product>();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isTargetPestDrawerVisible, setIsTargetPestDrawerVisible] = useState(
    false
  );

  const { tankCount, isTankInfoEnabled } = application || {};

  const {
    onAppliedAreaCalculation,
    onAppliedPercentageCalculation
  } = useProductCalculations({ tankCount, isTankInfoEnabled });

  // Determine if this is a liquid product with Tank Info enabled
  const isLiquidWithTankInfoEnabled = isLiquidWithTankInfo(
    productToAdd.stdunit,
    application?.isTankInfoEnabled
  );

  // Disable fields when product is carrier OR liquid product with Tank Info ON
  // Coverage enforcement (100%) is handled at the ProductsStep level when Tank Info is toggled
  const isAppliedAcresDisabled =
    productToAdd.isCarrier || isLiquidWithTankInfoEnabled;

  const handleTargetPestEdit = () => {
    setIsTargetPestDrawerVisible(true);
  };

  const handleTargetPestSave = (pestObjects: PestOption[]) => {
    // Store the pest objects directly in the correct field name
    setFieldValue('targetPests', pestObjects);
    setIsTargetPestDrawerVisible(false);
  };

  // Helper function to format the target pest display value
  const getTargetPestDisplayValue = () => {
    const { targetPests } = productToAdd;

    // targetPests is now always a PestOption array
    if (Array.isArray(targetPests) && targetPests.length > 0) {
      if (targetPests.length === 1) {
        // Single pest: display the name (truncate if too long to prevent scrolling)
        const pestName = targetPests[0].name;
        return pestName.length > 20
          ? `${pestName.substring(0, 20)}...`
          : pestName;
      }
      // Multiple pests: display count
      return `${targetPests.length} pests selected`;
    }

    return '';
  };

  const handleTargetPestCancel = () => {
    setIsTargetPestDrawerVisible(false);
  };

  // Helper function to get selected pest for the drawer
  const getSelectedPestForDrawer = () => {
    // targetPest is now always a PestOption array
    return productToAdd.targetPests || [];
  };

  const handleTargetPestKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar'
    ) {
      event.preventDefault();
      handleTargetPestEdit();
    }
  };

  return (
    <>
      <section data-testid="product-other-section" className={className}>
        <FormSectionHeader title="Other" />

        <div className="grid grid-cols-2 gap-4 mb-6">
          <FormInputWithPrecisionDisplay
            isRequired
            label="Product Applied Acres"
            tooltip={APPLIED_AREA_TOOLTIP_TEXT}
            fieldName="appliedAreaValue"
            inputSuffix="ac"
            onControlledBlur={onAppliedAreaCalculation}
            disabled={isAppliedAcresDisabled}
          />

          <FormInputWithPrecisionDisplay
            isRequired
            label="Applied Acre Percent"
            tooltip={APPLIED_AREA_TOOLTIP_TEXT}
            tooltipPlacement="topRight"
            tooltipAlign={{ offset: [15, 0] }}
            round={2}
            precision={2}
            fieldName="coveragePercent"
            inputSuffix="%"
            onControlledBlur={onAppliedPercentageCalculation}
            disabled={isAppliedAcresDisabled}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 items-center">
          <div className="w-full">
            <div className="syt-antd-input-label">
              <label
                htmlFor="applicationMethod"
                className="block text-sm font-medium text-neutral-60"
              >
                Application Method
              </label>
            </div>

            <Select
              data-testid="application-method-select"
              showSearch
              allowClear={!!productToAdd.applicationMethod}
              id="applicationMethod"
              value={productToAdd.applicationMethod}
              onChange={value =>
                setFieldValue('applicationMethod', value ?? '')
              }
              className="custom-select w-full"
              suffixIcon={
                isDropdownVisible ? (
                  <ChevronUp width={18} height={18} />
                ) : (
                  <ChevronDown width={18} height={18} />
                )
              }
              onDropdownVisibleChange={visible => setIsDropdownVisible(visible)}
              getPopupContainer={getDropdownParentWithoutDetaching}
            >
              {applicationMethodOptions.map(method => (
                <Select.Option key={method.value} value={method.value}>
                  {method.label}
                </Select.Option>
              ))}
            </Select>
          </div>

          <Input
            id="targetPest"
            name="targetPest"
            label="Target Pest"
            value={getTargetPestDisplayValue()}
            onChange={handleChange}
            onClick={handleTargetPestEdit}
            onKeyDown={handleTargetPestKeyDown}
            placeholder="No pest selected"
            readOnly
            className="target-pest-input-wrapper"
            suffix={
              <Button
                type={ButtonType.link}
                icon={<EditIcon />}
                tabIndex={-1}
              />
            }
          />
        </div>
      </section>

      <TargetPestSelectionDrawer
        visible={isTargetPestDrawerVisible}
        zIndex={1001}
        selectedPest={getSelectedPestForDrawer()}
        onClose={handleTargetPestCancel}
        onSave={handleTargetPestSave}
      />
    </>
  );
};

export default ProductOtherSection;
